import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ADDED_DISPLAY_MS = 1600;

/**
 * Quick size add on product cards. "+" opens the size bar (pointer devices also open it on hover via
 * CSS); clicking a size adds that variant to the cart. It follows the product form's cart flow: a
 * CartLinesUpdateEvent whose promise resolves with the refreshed cart and section HTML, so the cart
 * drawer, cart items and cart icon update (and the drawer auto-opens when that setting is on).
 *
 * @typedef {object} QuickSizeAddRefs
 * @property {HTMLButtonElement} [toggle] - The "+" button that opens the size bar
 * @property {HTMLElement} [panel] - The size bar
 * @property {HTMLElement} [status] - Live region for the "added" message
 *
 * @extends {Component<QuickSizeAddRefs>}
 */
class QuickSizeAddComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    document.addEventListener(
      'click',
      (event) => {
        if (event.target instanceof Node && !this.contains(event.target)) this.#setOpen(false);
      },
      { signal: this.#abortController.signal }
    );
    this.addEventListener('keydown', (event) => event.key === 'Escape' && this.#setOpen(false), {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    clearTimeout(this.#timer);
  }

  toggle() {
    this.#setOpen(!this.classList.contains('is-open'));
  }

  /** @param {MouseEvent} event - Its target is the clicked size (or "+") button */
  async add(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement) || button.disabled || this.dataset.state === 'adding') return;
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    this.dataset.state = 'adding';

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const body = new FormData();
      body.set('id', variantId);
      body.set('quantity', '1');
      if (sectionIds.length) body.set('sections', sectionIds.join(','));

      const config = fetchConfig('javascript', { body });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      }).then((res) => res.json());

      const cart = await this.#fetchCart();
      const didError = Boolean(response.status);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'quick-size-add',
          sourceId: variantId,
          itemCount: 1,
          sections: response.sections,
          didError,
        },
      });

      if (didError) {
        this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
        this.#showStatus(response.message || '');
      } else {
        this.#showStatus(Theme.translations?.added || '✓');
      }
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: String(error), code: 'SERVICE_UNAVAILABLE' }));
      delete this.dataset.state;
    }
  }

  /** @param {boolean} open */
  #setOpen(open) {
    const { toggle } = this.refs;
    this.classList.toggle('is-open', open);
    toggle?.setAttribute('aria-expanded', String(open));
  }

  /** @param {string} message */
  #showStatus(message) {
    const { status } = this.refs;
    if (status) status.textContent = message;
    this.dataset.state = 'added';
    this.#setOpen(false);
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => {
      delete this.dataset.state;
      if (status) status.textContent = '';
    }, ADDED_DISPLAY_MS);
  }

  async #fetchCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems && 'fetchCartData' in cartItems && typeof cartItems.fetchCartData === 'function') {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((res) => res.json());
  }
}

if (!customElements.get('quick-size-add')) {
  customElements.define('quick-size-add', QuickSizeAddComponent);
}
